import React, { useMemo, memo } from 'react';
import { Box, CircularProgress, Typography, Paper, alpha, useTheme } from '@mui/material';
import { PamGrid, DateRenderer, CurrencyRenderer, BooleanRenderer, createNumberFormatter } from '@1823-partners/core';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

// Column configuration mappings
const COLUMN_CONFIG = {
  date: ['DATE', 'MODIFIED_DATE', 'ASOFDT'],
  currency: ['EST_PROCEEDS', 'ORIGINAL_FACE', 'CURRENT_FACE'],
  percentage: ['BASE_YIELD'],
  number: [
    'ORIGINAL_FACE', 'CURRENT_FACE', 'PRICE', 'EST_PROCEEDS',
    'MARKET_SPREAD', 'TREAS_SPREAD', 'BASE_YIELD', 'WAL', 'DURATION',
    'CURR_CREDIT_SUPPORT', 'SSFA_DETACHMENT_POINT', 'THICKNESS', 'OWN_PCT'
  ],
  boolean: ['ILLIQUID', 'FLOATER', 'OVERRIDE']
};

// Date-only fields (no time component) must render in the UTC frame so a
// 'YYYY-MM-DD' calendar date isn't shifted back a day for clients west of UTC.
// MODIFIED_DATE is a true timestamp ('...Z') and must keep local rendering.
const TIMESTAMP_DATE_FIELDS = ['MODIFIED_DATE'];

// Value formatters using core renderers as functions
const formatters = {
  date: (value, key) => {
    if (!value) return '';
    // Timestamp fields: keep local rendering (correct for instants).
    if (TIMESTAMP_DATE_FIELDS.includes(key)) {
      try {
        const date = new Date(value);
        if (isNaN(date.getTime())) return value;
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch {
        return value;
      }
    }
    // Date-only fields (DATE, ASOFDT, ...): render in UTC frame to avoid day-shift.
    const parsed = dayjs.utc(value);
    if (!parsed.isValid()) return value;
    return parsed.format('MMM D, YYYY');
  },

  currency: (params) => CurrencyRenderer({ ...params, currency: 'USD' }),

  percentage: (value) => {
    if (value == null) return '';
    return (value * 100).toFixed(3) + '%';
  },

  number: (params) => createNumberFormatter(4)(params),

  boolean: (params) => BooleanRenderer(params)
};

// Date filter comparator
const dateComparator = (filterDate, cellValue) => {
  if (!cellValue) return -1;

  let cellDate;
  if (cellValue instanceof Date) {
    cellDate = new Date(cellValue);
  } else if (typeof cellValue === 'string') {
    cellDate = new Date(cellValue);
  }

  if (!cellDate || isNaN(cellDate.getTime())) return 0;

  cellDate.setHours(0, 0, 0, 0);
  filterDate.setHours(0, 0, 0, 0);

  if (cellDate < filterDate) return -1;
  if (cellDate > filterDate) return 1;
  return 0;
};

// Get column type
const getColumnType = (key) => {
  if (COLUMN_CONFIG.date.includes(key) || key.toLowerCase().includes('date')) return 'date';
  if (COLUMN_CONFIG.currency.includes(key)) return 'currency';
  if (COLUMN_CONFIG.percentage.includes(key)) return 'percentage';
  if (COLUMN_CONFIG.number.includes(key)) return 'number';
  if (COLUMN_CONFIG.boolean.includes(key)) return 'boolean';
  return 'text';
};

// Column header names mapping
const COLUMN_HEADERS = {
  'DATE': 'Trade Date',
  'MODIFIED_DATE': 'Modified Date',
  'ASOFDT': 'As Of Date',
  'CUSIP': 'CUSIP',
  'DEAL_NAME': 'Deal Name',
  'TRANCHE': 'Tranche',
  'DIRECTION': 'Direction',
  'CURRENCY': 'Currency',
  'SEC_OWNER': 'Security Owner',
  'SECURITY_OWNER': 'Security Owner',
  'SEC_OWNER_TEAM': 'Security Owner Team',
  'TYPE': 'Type',
  'COUNTERPARTY': 'Counterparty',
  'CONNECTED_ENTITY': 'Connected Entity',
  'DESCRIPTION': 'Description',
  'ORIGINAL_FACE': 'Original Face',
  'CURRENT_FACE': 'Current Face',
  'PRICE': 'Price',
  'EST_PROCEEDS': 'Est Proceeds',
  'MARKET_SPREAD': 'Market Convention Spread',
  'TREAS_SPREAD': 'Treasury Equivalent Spread',
  'BASE_YIELD': 'Base Case Yield',
  'WAL': 'WAL',
  'DURATION': 'Duration',
  'ILLIQUID': 'Illiquid (Y/N)',
  'FLOATER': 'Floater (Y/N)',
  'OVERRIDE': 'Override (Y/N)',
  'RATING': 'Rating at Trade',
  'CURR_CREDIT_SUPPORT': 'Current Credit Support',
  'SSFA_DETACHMENT_POINT': 'SSFA Detachment Point',
  'THICKNESS': 'Thickness',
  'ACCOUNT': 'Account',
  'NOTES': 'Notes',
  'MODIFIED_BY': 'Modified By',
  'OWN_PCT': 'Own %'
};

// Column header tooltips mapping
const COLUMN_TOOLTIPS = {
  'DATE': 'The date when the trade was executed',
  'MODIFIED_DATE': 'Date and time when this entry was last modified',
  'ASOFDT': 'As-of date for this security data',
  'CUSIP': 'Committee on Uniform Securities Identification Procedures number - unique identifier for the security',
  'DEAL_NAME': 'The name of the deal or security',
  'TRANCHE': 'The tranche identifier for structured securities',
  'DIRECTION': 'Whether this is a Buy or Sell transaction',
  'CURRENCY': 'The currency denomination for this security',
  'SEC_OWNER': 'The person or entity responsible for managing this security',
  'SECURITY_OWNER': 'The person or entity responsible for managing this security',
  'SEC_OWNER_TEAM': 'The team responsible for managing this security (CRE, CREDIT, OCIO, PSP, SP)',
  'TYPE': 'The type/category of security (e.g., Corporate Bond, Municipal, etc.)',
  'COUNTERPARTY': 'The other party involved in the transaction',
  'CONNECTED_ENTITY': 'Any related entity or subsidiary connected to the counterparty',
  'DESCRIPTION': 'Additional descriptive information about the security',
  'ORIGINAL_FACE': 'The original principal amount of the security when first issued',
  'CURRENT_FACE': 'The current outstanding principal amount of the security',
  'PRICE': 'The price at which the security was traded',
  'EST_PROCEEDS': 'Estimated proceeds from the trade (calculated as Price x Current Face / 100)',
  'MARKET_SPREAD': 'The spread over the benchmark using market conventions',
  'TREAS_SPREAD': 'The spread over Treasury securities with equivalent duration',
  'BASE_YIELD': 'The expected yield under base case assumptions',
  'WAL': 'Weighted Average Life - average time until principal is repaid',
  'DURATION': 'Measure of price sensitivity to interest rate changes',
  'ILLIQUID': 'Indicates if the security is private or hard to trade (at discretion of investment team)',
  'FLOATER': 'Indicates if this is a floating rate security (fixed-to-float should be N)',
  'OVERRIDE': 'Indicates if this entry has pricing or valuation overrides (used for Surveillance process)',
  'RATING': 'The credit rating of the security at the time of trade',
  'CURR_CREDIT_SUPPORT': 'Current level of credit enhancement or subordination',
  'SSFA_DETACHMENT_POINT': 'Simplified Supervisory Formula Approach detachment point for capital calculation',
  'THICKNESS': 'The thickness of the tranche (calculated as SSFA Detachment Point - Current Credit Support)',
  'ACCOUNT': 'The account or entity that owns this position',
  'NOTES': 'Additional comments or notes about this entry',
  'MODIFIED_BY': 'User who last modified this entry',
  'OWN_PCT': 'Ownership percentage of the position'
};

// Pinned columns (left side)
const PINNED_COLUMNS = ['DATE', 'ASOFDT', 'CUSIP', 'DEAL_NAME'];

// Create column definition
const createColumnDef = (key) => {
  const type = getColumnType(key);

  const baseDef = {
    headerName: COLUMN_HEADERS[key] || key,
    headerTooltip: COLUMN_TOOLTIPS[key] || `${key} column`,
    field: key,
    sortable: true,
    resizable: true,
    minWidth: 100,
    pinned: PINNED_COLUMNS.includes(key) ? 'left' : null
  };

  // Add type-specific configuration
  switch (type) {
    case 'date':
      return {
        ...baseDef,
        filter: 'agDateColumnFilter',
        valueFormatter: (params) => formatters.date(params.value, key),
        filterParams: {
          comparator: dateComparator,
          browserDatePicker: true
        },
        width: 120
      };

    case 'currency':
      return {
        ...baseDef,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        cellRenderer: CurrencyRenderer,
        width: 150
      };

    case 'percentage':
      return {
        ...baseDef,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        valueFormatter: (params) => formatters.percentage(params.value),
        width: 130
      };

    case 'number':
      return {
        ...baseDef,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        valueFormatter: createNumberFormatter(4),
        width: 130
      };

    case 'boolean':
      return {
        ...baseDef,
        filter: true,
        cellRenderer: BooleanRenderer,
        width: 100
      };

    default:
      // Special handling for specific text fields
      if (key === 'NOTES' || key === 'DESCRIPTION') {
        return {
          ...baseDef,
          filter: true,
          minWidth: 200,
          flex: 1
        };
      }
      return {
        ...baseDef,
        filter: true
      };
  }
};

const BlotterGrid = memo(function BlotterGrid({ data, loading, columnDefs = null }) {
  const theme = useTheme();

  // Generate column definitions from data
  const finalColumnDefs = useMemo(() => {
    if (columnDefs) return columnDefs;
    if (!data || data.length === 0) return [];
    return Object.keys(data[0]).map(createColumnDef);
  }, [data, columnDefs]);

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 6,
          bgcolor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.default, 0.3)
            : alpha('#f8fafc', 0.8)
        }}
      >
        <CircularProgress size={40} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 6,
          bgcolor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.default, 0.3)
            : alpha('#f8fafc', 0.8),
          borderRadius: 2,
          border: `1px dashed ${theme.palette.divider}`
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '14px',
            fontStyle: 'italic'
          }}
        >
          No blotter data available. Select a deal and tranches to load data.
        </Typography>
      </Box>
    );
  }

  // Grid with data
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'auto',
        maxHeight: 500
      }}
    >
      <PamGrid
        rowData={data}
        columnDefs={finalColumnDefs}
        height={Math.min(200 + (data.length * 36), 460)}
        showQuickFilter={false}
        showFooter={false}
        showToolbar={false}
        showExport={false}
        defaultColDef={{
          sortable: true,
          resizable: true,
          editable: false,
        }}
        rowHeight={36}
        headerHeight={42}
        suppressRowClickSelection
        animateRows={false}
        suppressColumnVirtualisation={false}
        suppressRowVirtualisation={false}
      />
    </Paper>
  );
});

export default BlotterGrid;
